/**
 * ICD-10 code processing helpers.
 *
 * Besides extracting the general/specific diagnosis categories out of ICD-10
 * display values, this module provides functions to work with the ICD-10
 * hierarchy: a parent code (e.g. 'C24') has child codes ('C24.0', 'C24.1', ...)
 * which can be resolved from a FHIR ValueSet by prefix matching.
 */

/**
 * A single entry of a FHIR ValueSet, optionally carrying the separated
 * general/specific ICD information derived from the display value.
 */
export interface IcdValueSetEntry {
  code: string;
  display: string;
  system?: string;
  icdGeneral?: string;
  icdSpecific?: string;
}

/**
 * Assumes the icd code entry has the structure '<general diagnosis category>|<specific diagnosis subcategory>'.
 * Cuts the string at the first "|" found and returns the first substring.
 * If no "|" is found, the original string is returned.
 * @param icdString display value of an ICD-10 entry
 * @returns the general diagnosis category
 */
const extractIcdGeneral = function (icdString: string): string {
  const pipePosition = icdString.indexOf('|');
  if (pipePosition > -1) {
    //"|" found
    return icdString.slice(0, pipePosition);
  } else {
    return icdString;
  }
};

/**
 * Assumes the icd code entry has the structure '< general diagnosis category >|< specific diagnosis subcategory >'.
 * Cuts the string at the first "|" found and returns the second substring.
 * If no "|" is found, the original string is returned.
 * @param icdString display value of an ICD-10 entry
 * @returns the specific diagnosis subcategory, or the input if it is falsy
 */
const extractIcdSpecific = function (icdString?: string): string | undefined {
  if (!icdString) {
    return icdString;
  }
  const pipePosition = icdString.indexOf('|');
  if (pipePosition > -1) {
    //"|" found
    return icdString.slice(pipePosition + 1, icdString.length);
  } else {
    return icdString;
  }
};

/**
 * Processes given ValueSet and returns a copy where each entry has two additional properties:
 * the specific and the general icd category, extracted from the display value.
 * @param originalConditionVs value set of condition (ICD-10) entries
 * @returns processed ValueSet
 */
const getConditionValueSetWithSeparatedGeneralAndSpecificInfo = function (
  originalConditionVs: IcdValueSetEntry[]
): IcdValueSetEntry[] {
  const processedVs = originalConditionVs.map((vsEntry) => {
    return {
      ...vsEntry,
      icdGeneral: extractIcdGeneral(vsEntry.display),
      icdSpecific: extractIcdSpecific(vsEntry.display),
    };
  });

  return processedVs;
};

/**
 * Returns the codes of all ValueSet entries that are descendants of the given parent code.
 * A descendant is any entry whose code starts with `parentCode + '.'`. The explicit dot
 * boundary prevents e.g. 'C2' from matching 'C24'. All nesting depths are included
 * (e.g. 'Z00' matches 'Z00.0' as well as 'Z00.00').
 * @param parentCode ICD-10 parent code, e.g. 'C24'
 * @param valueSet ValueSet entries to search
 * @returns codes of all descendants in the order they appear in the valueSet; empty array if there are none
 */
const getIcdChildrenCodes = function (
  parentCode: string,
  valueSet: IcdValueSetEntry[]
): string[] {
  return valueSet
    .filter((entry) => entry.code.startsWith(parentCode + '.'))
    .map((entry) => entry.code);
};

/**
 * Checks whether the given code exists in the ValueSet and has at least one child code.
 * @param code ICD-10 code to check, e.g. 'C24'
 * @param valueSet ValueSet entries to search
 * @returns true iff the code is present in the valueSet and at least one entry is a descendant of it
 */
const isIcdParentCode = function (
  code: string,
  valueSet: IcdValueSetEntry[]
): boolean {
  const codeExistsInValueSet = valueSet.some((entry) => entry.code === code);
  return codeExistsInValueSet && getIcdChildrenCodes(code, valueSet).length > 0;
};

/**
 * Returns the full ValueSet entry objects of all descendants of the given parent code.
 * See {@link getIcdChildrenCodes} for the matching semantics.
 * @param parentCode ICD-10 parent code, e.g. 'C24'
 * @param valueSet ValueSet entries to search
 * @returns all child entries in the order they appear in the valueSet; empty array if there are none
 */
const getIcdChildrenEntries = function (
  parentCode: string,
  valueSet: IcdValueSetEntry[]
): IcdValueSetEntry[] {
  return valueSet.filter((entry) => entry.code.startsWith(parentCode + '.'));
};

/**
 * Collects all codes in the given ValueSet that have at least one child code.
 * @param valueSet ValueSet entries to search
 * @returns set of all parent codes (codes that are a dot-prefix of at least one other entry's code)
 */
const getIcdParentCodes = function (
  valueSet: IcdValueSetEntry[]
): Set<string> {
  const parentCodes = new Set<string>();
  valueSet.forEach((entry) => {
    const hasChild = valueSet.some((other) =>
      other.code.startsWith(entry.code + '.')
    );
    if (hasChild) {
      parentCodes.add(entry.code);
    }
  });
  return parentCodes;
};

export default {
  extractIcdGeneral,
  extractIcdSpecific,
  getConditionValueSetWithSeparatedGeneralAndSpecificInfo,
  getIcdChildrenCodes,
  isIcdParentCode,
  getIcdChildrenEntries,
  getIcdParentCodes,
};
